// src/pages/EmailTemplatesPage.tsx
//
// ★ PHASE 6, TASK 24 — Annex A story 33: "Email Template Management".
//
// ⚠★ **THE PROSE ON THIS SCREEN WAS FOUR TASKS STALE, AND THIS FILE'S HEADER
// WAS THE ONLY PART THE WAVE FIXED.** The paragraph a PSD user actually reads
// still said, in bold, *"no delivery mechanism exists anywhere in this system"*
// and *"like every other notification in this build, recorded and never sent"*
// — on the surface that AUTHORS the content that gets sent, one click away from
// a Notifications screen showing SIMULATED rows and naming the transport.
//
// The screen is still template administration only: saving, activating or
// exporting a template sends nothing, and that part was never in doubt. What
// was wrong was the absolute claim ABOUT THE REST OF THE SYSTEM appended to it.
// Whether a template ever reaches anybody is a DEPLOYMENT fact
// (`notificationDelivery`), so it is now DERIVED from the gateway's own report
// of what it wired — the same discipline `NotificationsPage` and
// `routes/notifications.ts` already apply, and the same reason: a hardcoded
// honesty notice is a claim nobody keeps true (S-016).
import { CodeBadge, StateBadge, stateLabel } from '../components/StatusBadge'
import { useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { VersionHistory } from '../components/VersionHistory'
import { FormResult } from '../components/FormResult'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { EMAIL_TEMPLATE_STATUSES, type EmailTemplateStatusValue } from '../lib/types'
import { discloseNotificationDelivery } from '../lib/environment'
import { useAuth } from '../state/AuthContext'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

/**
 * What this screen may say about whether a template it manages can ever reach
 * anybody, narrowed through the same disclosure the sign-in banner uses.
 *
 * ⚠ **No branch here is a reassurance about the SYSTEM**, only about this
 * deployment, and the two ambiguous states say what they cannot tell rather than
 * falling to the comfortable answer — the asymmetry `paymentAgencyClause`
 * (`lib/environment.ts`) established for the sibling capability.
 */
function deliveryReachClause(report: Parameters<typeof discloseNotificationDelivery>[0]): string {
  const disclosure = discloseNotificationDelivery(report)
  switch (disclosure.kind) {
    case 'contained':
      return disclosure.reported === 'none'
        ? 'The gateway reports that no mail transport is configured in this deployment, so a notification built from a template here is recorded and never delivered: that is a deployment fact, not a property of this build.'
        : 'The gateway reports a SIMULATED mail transport: a notification built from a template here is recorded with a receipt no relay issued, and nothing leaves the building.'
    case 'reaching':
      return 'The gateway reports a LIVE outbound mail relay: a notification built from a template here can be delivered to a real recipient.'
    case 'absent':
      return 'The gateway answered without saying which mail transport is configured, so this build cannot tell you that a notification built from a template here stops short of a real inbox: assume it does not.'
    case 'unrecognised':
      return `The gateway reports its mail transport as “${disclosure.reported}”, which is not a value this build recognises, so it cannot tell you that a notification built from a template here stops short of a real inbox: assume it does not.`
    case 'unknown':
      return 'This build has no report yet of which mail transport is configured, so it cannot tell you whether a notification built from a template here can reach a real inbox.'
  }
}

type EmailTemplateSortKey = 'templateCode' | 'templateName' | 'subjectLine' | 'status' | 'effectiveFrom'

const COLUMN_LABELS: Readonly<Record<EmailTemplateSortKey, string>> = {
  templateCode: 'Code',
  templateName: 'Name',
  subjectLine: 'Subject line',
  status: 'Status',
  effectiveFrom: 'Effective from',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  templateCode: 'Code',
  templateName: 'Name',
  subjectLine: 'Subject line',
  bodyContent: 'Email content',
  note: 'Note (optional)',
} as const

/**
 * ★★ WAVE 8 (Task 3) REVIEW FINDING — the bounded fields that ONLY EXIST ON
 * AN ESTABLISH, and are therefore not part of a RETIRE's problem set.
 *
 * ⚠ Ticking the "Retire … instead of superseding it" box unmounts them and drops
 * them from the request body, but the React state lives on this page and survives
 * the unmount. Without naming them here, a value typed before the box was ticked
 * keeps `problems.any` true and **disables Save with no error visible anywhere** —
 * the Wave 5 failure `components/FieldError.tsx`'s header records verbatim. See
 * `lib/published-bounds.ts`'s `BoundsProblemOptions` for the full reasoning.
 *
 * ⚠ This list is the SUBMITTED half of `handleSubmit`'s own `!retireInstead &&`
 * spread below. If one changes, the other has to.
 */
const ESTABLISH_ONLY_FIELDS = ['templateName', 'subjectLine', 'bodyContent'] as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

export function EmailTemplatesPage() {
  // ★★★ WAVE 12 (P4) — the version history and its bi-temporal rewind. The series
  // route has answered since Phase 6 and NOTHING called it until this wave; the
  // RTM's Annex A 26-28 row carried that as a gap, because an endpoint no client
  // calls is indistinguishable from one that does not work.
  //
  // ⚠ The fetcher RESOLVES TO NULL until a code is chosen rather than calling the
  // route with an empty key: that URL is the LIST route, which would return the
  // whole catalogue into a component that renders it as one code's history.
  const [historySubject, setHistorySubject] = useState('')
  const [historyAsRecordedAt, setHistoryAsRecordedAt] = useState('')
  const history = useApi(
    () =>
      historySubject.length === 0
        ? Promise.resolve(null)
        : api.emailTemplateSeries(historySubject, historyAsRecordedAt),
    [historySubject, historyAsRecordedAt],
  )
  // ⚠ Read from the SERVER's report on `/api/me`, held in `AuthContext`, never
  // from anything baked into this bundle — see that field's own docstring.
  const { notificationDelivery } = useAuth()
  const { sort, onSort, interacted } = useSort<EmailTemplateSortKey>()
  const templates = useApi(() => api.emailTemplates(sortParams(sort)), [sort.column, sort.dir])

  const [templateCode, setTemplateCode] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [subjectLine, setSubjectLine] = useState('')
  const [bodyContent, setBodyContent] = useState('')
  const [variables, setVariables] = useState('')
  const [category, setCategory] = useState('')
  const [status, setStatus] = useState<EmailTemplateStatusValue>('ACTIVE')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const rows = templates.data?.templates ?? []
  const total = templates.data?.total ?? 0

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = templates.data?.validation
  const problems = boundsProblems(
    validation,
    {
      templateCode: templateCode.trim().toUpperCase(),
      templateName: templateName.trim(),
      subjectLine: subjectLine.trim(),
      bodyContent: bodyContent.trim(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
    // ⚠ A RETIRE sends none of the establish-only fields, so none of them can be
    // in breach — see ESTABLISH_ONLY_FIELDS above for the disabled-with-no-message
    // defect this closes.
    { notSubmitted: retireInstead ? ESTABLISH_ONLY_FIELDS : [] },
  )

  const canSubmit =
    templateCode.trim().length > 0 &&
    effectiveFrom.length > 0 &&
    !problems.any &&
    (retireInstead ||
      (templateName.trim().length > 0 && subjectLine.trim().length > 0 && bodyContent.trim().length > 0))

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). Composed once, from the fields as they were when Save was pressed —
   * never re-derived from a form the officer may since have edited.
   *
   * ⚠ Both sentences repeat that saving sends nothing: the one fact this screen's
   * header records officers being misled about.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const upper = templateCode.trim().toUpperCase()
    setConfirming(
      retireInstead
        ? `Retire email template ${upper} from ${effectiveFrom}. No successor opens, so from that day no template is in force under that code and a notification that needs it cannot be built. Nothing is sent by this. There is no undo. A mistake is corrected by another version.`
        : `Supersede email template ${upper} from ${effectiveFrom}: "${templateName.trim()}", subject "${subjectLine.trim()}", status ${stateLabel(status).toLowerCase()}. Notifications built from that day use this content. Nothing is sent by saving. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeEmailTemplate(templateCode.trim().toUpperCase(), {
        effectiveFrom,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!retireInstead && {
          templateName: templateName.trim(),
          subjectLine: subjectLine.trim(),
          bodyContent: bodyContent.trim(),
          variables: variables.trim().length > 0 ? variables.trim() : null,
          category: category.trim().length > 0 ? category.trim() : null,
          status,
        }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${templateCode.trim().toUpperCase()} retired from ${effectiveFrom}.`
            : `${templateCode.trim().toUpperCase()} superseded from ${effectiveFrom}. Nothing was sent.`,
      })
      setTemplateCode('')
      setTemplateName('')
      setSubjectLine('')
      setBodyContent('')
      setVariables('')
      setCategory('')
      setStatus('ACTIVE')
      setEffectiveFrom('')
      setNote('')
      templates.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this template.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Email templates</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            The email template catalogue. <strong>This is content administration only:</strong>{' '}
            saving, activating or downloading a template never sends an email. The notifiable events these
            templates serve (for example a new eCS entitlement) are queued on the outbox and attempted only when an operator
            runs it: see <strong>eCS entitlement notifications</strong> for the queue itself. {deliveryReachClause(notificationDelivery)}{' '}
            Effective-dated, PSD-only.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.emailTemplateExportUrl(format)
            }}
            email={<EmailReportButton path="/api/administration/email-templates/export" />}
          />
        </div>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={templates.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {templates.error !== null ? (
        <ErrorCard title="Could not load email templates" message={templates.error} onRetry={templates.reload} />
      ) : templates.initialLoading ? (
        <LoadingCard label="Loading email templates" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No templates in scope"
          message="Either nothing is currently in force, or your active identity is not PSD: this catalogue is administered by PSD only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={templates.loading}>
            <table className="pt-table">
              <caption className="sr-only">Email templates in force, with code, name, subject line, status and effective date</caption>
              <thead>
                <tr>
                  <SortableTh label="Code" sortKey="templateCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Name" sortKey="templateName" sort={sort} onSort={onSort} />
                  <SortableTh label="Subject line" sortKey="subjectLine" sort={sort} onSort={onSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td><CodeBadge value={row.templateCode} /></td>
                    <td>{row.templateName}</td>
                    <td className="text-sm">{row.subjectLine}</td>
                    <td>
                      <StateBadge value={row.status} />
                    </td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.effective.from)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} currently in-force template{total === 1 ? '' : 's'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
        </div>
      )}

      {/* ★★★ WAVE 12 (P4) — the version history, below the catalogue it belongs to.
          The code picker lives in the component rather than as a column on this
          table: twelve tables of differing shapes meant twelve thead/tbody edits,
          twelve chances to break a working screen for one control. */}
      <VersionHistory
        subject={historySubject}
        subjectOptions={rows.map((row) => row.templateCode)}
        subjectHint="The email template codes listed above. Sort or filter that table to reach others."
        onSubjectChange={(next) => {
          // ⚠ Changing code RESETS the rewind: an instant chosen while reading one
          // code is not a statement about another, and carrying it over would
          // silently reconstruct the new one at a forgotten instant.
          setHistoryAsRecordedAt('')
          setHistorySubject(next)
        }}
        entries={history.data?.entries ?? []}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        asRecordedAt={historyAsRecordedAt}
        onAsRecordedAtChange={setHistoryAsRecordedAt}
        columns={[
            { header: 'Name', cell: (entry) => entry.templateName },
            { header: 'Status', cell: (entry) => <StateBadge value={entry.status} /> },
        ]}
      />
      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Compose or retire a template</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            Superseding is the only way a template changes: there is no edit-in-place.{' '}
            <strong>Saving here never sends anything.</strong>
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <BoundedField
            label={BOUNDED_FIELDS.templateCode}
            example="e.g. ECS_NEW_ENTITLEMENT."
            value={templateCode}
            onValueChange={setTemplateCode}
            bounds={validation?.templateCode}
            problem={problems.of.templateCode}
            required
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Effective from <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Retire this template instead of superseding it (no successor opens)
          </label>
          {!retireInstead && (
            <>
              <BoundedField
                label={BOUNDED_FIELDS.templateName}
                value={templateName}
                onValueChange={setTemplateName}
                bounds={validation?.templateName}
                problem={problems.of.templateName}
                required
              />
              <BoundedField
                label={BOUNDED_FIELDS.subjectLine}
                value={subjectLine}
                onValueChange={setSubjectLine}
                bounds={validation?.subjectLine}
                problem={problems.of.subjectLine}
                required
              />
              <SgdsSelect label="Status" value={status} onSgdsChange={(e) => setStatus(targetValue(e) as EmailTemplateStatusValue)}>
                {EMAIL_TEMPLATE_STATUSES.map((s) => (
                  <SgdsSelectOption key={s} value={s}>
                    {stateLabel(s)}
                  </SgdsSelectOption>
                ))}
              </SgdsSelect>
              <SgdsInput placeholder="" label="Category (optional)" value={category} onSgdsInput={(e) => setCategory(targetValue(e))} />
              <div className="sm:col-span-2">
                <SgdsInput placeholder=""
                  label="Variables/placeholders (optional)"
                  hintText="e.g. {{officerName}}, {{entitlementDate}}"
                  value={variables}
                  onSgdsInput={(e) => setVariables(targetValue(e))}
                />
              </div>
              <BoundedField
                className="sm:col-span-2"
                label={BOUNDED_FIELDS.bodyContent}
                value={bodyContent}
                onValueChange={setBodyContent}
                bounds={validation?.bodyContent}
                problem={problems.of.bodyContent}
                multiline
                rows={8}
                required
              />
            </>
          )}
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Retire' : 'Save (nothing is sent)'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
